export const BLOG_SIDEBAR_STORAGE_KEY = "druyp-blog-recent-posts-visible";

export const BLOG_CARD_THEMES = [
  { bg: "#EFF6FF", color: "#2563EB", label: "Insights" },
  { bg: "#F5F3FF", color: "#7C3AED", label: "Guides" },
  { bg: "#ECFDF5", color: "#059669", label: "Tips" },
  { bg: "#FFF7ED", color: "#EA580C", label: "Updates" },
  { bg: "#FDF2F8", color: "#DB2777", label: "Stories" },
  { bg: "#F0F9FF", color: "#0284C7", label: "Blog" },
];

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const formatBlogDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

export const estimateReadTime = (content) => {
  const words = stripHtml(content).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
};

export const BLOG_CARD_EXCERPT_MAX = 100;
export const BLOG_CARD_CATEGORY_MAX = 28;

export const getBlogExcerpt = (post, maxLength = BLOG_CARD_EXCERPT_MAX) => {
  if (post?.excerpt?.trim()) {
    const excerpt = post.excerpt.trim().replace(/\s+/g, " ");
    return excerpt.length > maxLength ? `${excerpt.slice(0, maxLength - 3)}...` : excerpt;
  }

  const plain = stripHtml(post?.content).replace(/\s+/g, " ");
  if (!plain) return "";
  return plain.length > maxLength ? `${plain.slice(0, maxLength - 3)}...` : plain;
};

export const getBlogCategory = (post, theme) => {
  const keyword = String(post?.focusKeyword || post?.focus_keyword || "").trim();
  if (keyword) {
    const primary = keyword.split(/[,;|]/).map((part) => part.trim()).find(Boolean) || keyword;
    const label = primary.toUpperCase();
    if (label.length <= BLOG_CARD_CATEGORY_MAX) {
      return label;
    }
    return `${label.slice(0, BLOG_CARD_CATEGORY_MAX - 3)}...`;
  }
  return theme.label.toUpperCase();
};

export const getBlogTheme = (index) =>
  BLOG_CARD_THEMES[index % BLOG_CARD_THEMES.length];

export const getBlogDate = (post) =>
  formatBlogDate(post?.publishAt || post?.createdAt);

export const BLOG_CARD_BANNER_ASPECT_CLASS = "aspect-[16/9]";

export const getBlogBanner = (post) => {
  const banner = String(post?.featuredImage || post?.featured_image || "").trim();
  return banner || "";
};

const STOP_WORDS = new Set([
  "about",
  "after",
  "again",
  "all",
  "also",
  "and",
  "any",
  "are",
  "been",
  "before",
  "being",
  "between",
  "both",
  "but",
  "can",
  "could",
  "did",
  "does",
  "doing",
  "each",
  "for",
  "from",
  "had",
  "has",
  "have",
  "here",
  "how",
  "into",
  "its",
  "just",
  "more",
  "most",
  "not",
  "now",
  "only",
  "other",
  "our",
  "out",
  "over",
  "same",
  "she",
  "should",
  "some",
  "such",
  "than",
  "that",
  "the",
  "their",
  "them",
  "then",
  "there",
  "these",
  "they",
  "this",
  "those",
  "through",
  "too",
  "under",
  "very",
  "was",
  "were",
  "what",
  "when",
  "where",
  "which",
  "while",
  "who",
  "why",
  "will",
  "with",
  "would",
  "you",
  "your",
]);

const WEAK_TOKENS = new Set([
  "about",
  "article",
  "articles",
  "based",
  "beginner",
  "beginners",
  "best",
  "blog",
  "complete",
  "content",
  "day",
  "engine",
  "engines",
  "essential",
  "first",
  "free",
  "get",
  "good",
  "great",
  "guide",
  "help",
  "home",
  "important",
  "improve",
  "information",
  "key",
  "last",
  "learn",
  "link",
  "links",
  "list",
  "main",
  "make",
  "need",
  "new",
  "online",
  "optimization",
  "optimize",
  "page",
  "pages",
  "place",
  "post",
  "posts",
  "ranking",
  "rankings",
  "read",
  "related",
  "right",
  "search",
  "site",
  "sites",
  "start",
  "step",
  "steps",
  "techniques",
  "test",
  "time",
  "tips",
  "top",
  "topic",
  "topics",
  "use",
  "used",
  "using",
  "visibility",
  "way",
  "web",
  "website",
  "websites",
  "work",
  "works",
  "working",
  "year",
]);

const TOPIC_SHORT_TOKENS = new Set(["seo", "geo", "ai", "ux", "cms"]);

const tokenize = (text) =>
  stripHtml(text)
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .split(/\s+/)
    .map((word) => word.trim())
    .filter((word) => word.length >= 3 && !STOP_WORDS.has(word));

const getTopicPhrases = (post) => {
  const phrases = [];
  const focus = String(post?.focusKeyword || "");

  focus.split(/[,;|]/).forEach((part) => {
    const trimmed = part.trim().toLowerCase();
    if (trimmed.length >= 4) {
      phrases.push(trimmed);
    }
  });

  return phrases;
};

const getSignatureTokens = (post) => {
  const tokens = new Set();

  tokenize(post?.title).forEach((token) => {
    if (!WEAK_TOKENS.has(token) || TOPIC_SHORT_TOKENS.has(token)) {
      tokens.add(token);
    }
  });

  tokenize(post?.focusKeyword).forEach((token) => {
    if (!WEAK_TOKENS.has(token) || TOPIC_SHORT_TOKENS.has(token)) {
      tokens.add(token);
    }
  });

  tokenize(post?.excerpt).slice(0, 6).forEach((token) => {
    if (!WEAK_TOKENS.has(token) || TOPIC_SHORT_TOKENS.has(token)) {
      tokens.add(token);
    }
  });

  return tokens;
};

const countSharedTokens = (leftTokens, rightTokens) => {
  let count = 0;

  for (const token of leftTokens) {
    if (rightTokens.has(token)) {
      count += 1;
    }
  }

  return count;
};

const hasPhraseOverlap = (currentPost, otherPost) => {
  const otherHead = `${otherPost?.title || ""} ${otherPost?.focusKeyword || ""}`.toLowerCase();
  const currentHead = `${currentPost?.title || ""} ${currentPost?.focusKeyword || ""}`.toLowerCase();

  return (
    getTopicPhrases(currentPost).some(
      (phrase) => phrase.length >= 5 && otherHead.includes(phrase)
    ) ||
    getTopicPhrases(otherPost).some(
      (phrase) => phrase.length >= 5 && currentHead.includes(phrase)
    )
  );
};

const hasStrongTokenOverlap = (leftTokens, rightTokens) => {
  for (const token of leftTokens) {
    if (!rightTokens.has(token)) {
      continue;
    }

    if (token.length >= 5 || TOPIC_SHORT_TOKENS.has(token)) {
      return true;
    }
  }

  return false;
};

const isRelatedPost = (currentPost, otherPost) => {
  if (!currentPost || !otherPost) {
    return false;
  }

  if (currentPost.slug === otherPost.slug) {
    return true;
  }

  const currentTokens = getSignatureTokens(currentPost);
  const otherTokens = getSignatureTokens(otherPost);
  const sharedCount = countSharedTokens(currentTokens, otherTokens);

  if (sharedCount >= 2) {
    return true;
  }

  if (sharedCount >= 1 && hasStrongTokenOverlap(currentTokens, otherTokens)) {
    return true;
  }

  return hasPhraseOverlap(currentPost, otherPost);
};

export const getRelatedPosts = (posts, currentPost) => {
  if (!currentPost || !Array.isArray(posts) || posts.length === 0) {
    return [];
  }

  const relatedOthers = posts.filter(
    (post) => post.slug !== currentPost.slug && isRelatedPost(currentPost, post)
  );

  if (relatedOthers.length === 0) {
    return [];
  }

  return [currentPost, ...relatedOthers].sort((left, right) => {
    if (left.slug === currentPost.slug) return -1;
    if (right.slug === currentPost.slug) return 1;

    const leftDate = new Date(left.publishAt || left.createdAt || 0).getTime();
    const rightDate = new Date(right.publishAt || right.createdAt || 0).getTime();
    return rightDate - leftDate;
  });
};

/** @deprecated Use getRelatedPosts */
export const sortRelatedPosts = getRelatedPosts;
